/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  PbCcElementCsmRefDto,
  PbCcElementDto,
  PbCcElementJstRefDto,
  PbLinkCostCenterDto,
  PbLinkCsmDto,
  PbLinkCsmGetResponseDto,
  PbLinkCsmHeadingDto,
  PbLinkCsmItemDto,
  PbLinkCsmSubheadingDto,
  PbLinkJstDto,
  PbLinkJstGetResponseDto,
  PbLinkJstParagraphDto,
  PbLinkJstSectionDto,
  PbLinkSaveRequestDto,
  PbLinkSaveResponseDto,
} from '@gh/builder-shared-data';
import {
  createGroupIndexDriver,
  createOrderIndexDriver,
  createTableDriver,
  EntityConfig,
  entityValidatorByEntityConfig,
  fieldStateByEntityConfig,
  fieldValidatorByEntityConfig,
  fieldValidatorFrom,
  isEntityInvalid,
  nestedArrayValidator,
} from '@gh/common-shared';
import { ID, WithDirty, WithDirtyType, WithEntityState } from '@gh/common-shared-data';
import { composeComparators, isNotNil, valueComparator } from '@gh/core-util';
import { isNil, map, stubTrue } from 'lodash';

export enum PbLinkEntryPoint { Jst = 'Jst', Csm = 'Csm' }

export type PbLinkJstParagraph = PbLinkJstParagraphDto;
export type PbLinkJstSection = PbLinkJstSectionDto;
export type PbLinkJst = PbLinkJstDto;
export type PbLinkCcElementJstRef = WithDirtyType<WithEntityState<{
  specTemplateId: ID;
  paragraph: ID;
  section: ID;
}>>;
export type PbLinkJstRecord = WithDirtyType<WithEntityState<{
  recordId: ID;
  name: string;
  isActive: boolean;
  paragraph: { paragraphId?: ID };
  section: { paragraphId?: ID; sectionId?: ID };
}>>;

export type PbLinkCsmItem = PbLinkCsmItemDto;
export type PbLinkCsmSubheading = PbLinkCsmSubheadingDto;
export type PbLinkCsmHeading = PbLinkCsmHeadingDto;
export type PbLinkCsm = PbLinkCsmDto;
export type PbLinkCcElementCsmRef = WithDirtyType<WithEntityState<{
  csMasterId: ID;
  heading: ID;
  subheading: ID;
  item: ID;
}>>;
export type PbLinkCsmRecord = WithDirtyType<WithEntityState<{
  recordId: ID;
  name: string;
  isActive: boolean;
  heading: { headingId?: ID };
  subheading: { headingId?: ID; subheadingId?: ID };
  item: { headingId?: ID; subheadingId?: ID; itemId?: ID };
}>>;

export type PbLinkRecord = PbLinkJstRecord | PbLinkCsmRecord;

export type PbLinkCostCenter = WithDirty<PbLinkCostCenterDto & { preloaded: true }>;
export type PbLinkCcElement = WithEntityState<WithDirty<{
  itemId: ID;
  code: string;
  name: string;
  isComponent: boolean;
  costCenterId: ID;
  jstLayout: PbLinkCcElementJstRef[];
  csmLayout: PbLinkCcElementCsmRef[];
}>>;
export type PbLinkJstGetResponse = {
  priceBookId: ID;
  priceBookName: string;
  costCenters: PbLinkCostCenter[];
  jstLayout: PbLinkJst[];
};
export type PbLinkCsmGetResponse = {
  priceBookId: ID;
  priceBookName: string;
  costCenters: PbLinkCostCenter[];
  csmLayout: PbLinkCsm[];
};
export type PbLinkGetResponse = PbLinkJstGetResponse | PbLinkCsmGetResponse;

export type PbLinkSaveRequest = PbLinkCcElement[];
export type PbLinkSaveResponse = PbLinkSaveResponseDto;

export interface PbLinkMappingContext {
  getParentJstParagraph(sectionId: ID): PbLinkJstParagraph;

  getParentCsmSubheading(itemId: ID): PbLinkCsmSubheading;

  getParentCsmHeading(subheadingId: ID): PbLinkCsmHeading;

  getCostCenter(costCenterId: ID): PbLinkCostCenter;
}

export const pbLinkJstGetResponseFromDto = (dto: PbLinkJstGetResponseDto) => <PbLinkJstGetResponse><unknown>({
  priceBookId: dto.priceBookId,
  priceBookName: dto.priceBookName,
  costCenters: dto.costCenters.map((cc) => ({...cc, preloaded: false})),
  jstLayout: dto.specTemplates,
});

export const pbLinkCsmGetResponseFromDto = (dto: PbLinkCsmGetResponseDto) => <PbLinkCsmGetResponse><unknown>({
  priceBookId: dto.priceBookId,
  priceBookName: dto.priceBookName,
  costCenters: dto.costCenters.map((cc) => ({...cc, preloaded: false})),
  csmLayout: dto.colorSelections,
});

export const pbLinkJstCcElementsFromDto = (elements: PbCcElementDto[], mappingContext: PbLinkMappingContext) =>
  elements.map(({ itemId, code, name, isComponent, costCenterId, jobSpecLayout }) => <PbLinkCcElement>({
    itemId,
    code,
    name,
    isComponent,
    costCenterId,
    jstLayout: map(jobSpecLayout, (ref) => pbLinkCcElementJstRefFromDto(ref, mappingContext)),
  }));

export const pbLinkCsmCcElementsFromDto = (elements: PbCcElementDto[], mappingContext: PbLinkMappingContext) =>
  elements.map(({ itemId, code, name, isComponent, costCenterId, jobCsLayout }) => <PbLinkCcElement>({
    itemId,
    code,
    name,
    isComponent,
    costCenterId,
    csmLayout: map(jobCsLayout, (ref) => pbLinkCcElementCsmRefFromDto(ref, mappingContext)),
  }));

const pbLinkCcElementJstRefFromDto = (ref: PbCcElementJstRefDto, mappingContext: PbLinkMappingContext) => ({
  specTemplateId: ref.specTemplateId,
  // tslint:disable-next-line:no-non-null-assertion
  paragraph: ref.specTemplateParagraphId || mappingContext.getParentJstParagraph(ref.specTemplateSectionId!).id,
  section: ref.specTemplateSectionId,
});

const pbLinkCcElementCsmRefFromDto = (ref: PbCcElementCsmRefDto, mappingContext: PbLinkMappingContext) => {
  // tslint:disable-next-line:no-non-null-assertion
  const subheadingId = ref.csMasterSubHeadingId || mappingContext.getParentCsmSubheading(ref.csMasterItemId!).id;
  const heading = mappingContext.getParentCsmHeading(subheadingId);
  return {
    csMasterId: ref.csMasterId,
    heading: heading.id,
    subheading: subheadingId,
    item: ref.csMasterItemId,
  };
};

export const pbLinkSaveRequestToDto = (model: PbLinkSaveRequest, mappingContext: PbLinkMappingContext) =>
  <PbLinkSaveRequestDto>model.map(({ itemId, isComponent, code, costCenterId, jstLayout, csmLayout }) => ({
    itemId,
    costCenterId,
    isComponent,
    itemCode: code,
    costCenterCode: mappingContext.getCostCenter(costCenterId).code,
    jobSpecLayout: map(jstLayout, (ref) => <PbCcElementJstRefDto>({
      specTemplateId: ref.specTemplateId,
      specTemplateParagraphId: ref.paragraph,
      specTemplateSectionId: ref.section,
    })),
    jobCsLayout: map(csmLayout, (ref) => <PbCcElementCsmRefDto>({
      csMasterId: ref.csMasterId,
      csMasterSubHeadingId: ref.subheading,
      csMasterItemId: ref.item,
    })),
  }));

export const pbLinkJstRecordFromRef = (specTemplateId: ID,
                                       name: string,
                                       isActive: boolean,
                                       ref?: PbLinkCcElementJstRef) => ({
  recordId: specTemplateId,
  name,
  isActive,
  paragraph: {
    paragraphId: ref && ref.paragraph,
  },
  section: {
    paragraphId: ref && ref.paragraph,
    sectionId: ref && ref.section,
  },
  entityState: ref && ref.entityState,
  dirtyType: ref && ref.dirtyType,
});

export const pbLinkJstRecordToRef = (record: PbLinkJstRecord) => <PbLinkCcElementJstRef>({
  specTemplateId: record.recordId,
  paragraph: record.paragraph.paragraphId,
  section: record.section.sectionId,
  entityState: record.entityState,
  dirtyType: record.dirtyType,
});

export const pbLinkCsmRecordFromRef = (csMasterId: ID,
                                       name: string,
                                       isActive: boolean,
                                       ref?: PbLinkCcElementCsmRef) => ({
  recordId: csMasterId,
  name,
  isActive,
  heading: {
    headingId: ref && ref.heading,
  },
  subheading: {
    headingId: ref && ref.heading,
    subheadingId: ref && ref.subheading,
  },
  item: {
    headingId: ref && ref.heading,
    subheadingId: ref && ref.subheading,
    itemId: ref && ref.item,
  },
  entityState: ref && ref.entityState,
  dirtyType: ref && ref.dirtyType,
});

export const pbLinkCsmRecordToRef = (record: PbLinkCsmRecord) => <PbLinkCcElementCsmRef>({
  csMasterId: record.recordId,
  heading: record.heading.headingId,
  subheading: record.subheading.subheadingId,
  item: record.item.itemId,
  entityState: record.entityState,
  dirtyType: record.dirtyType,
});

export const isPbLinkCcNotPreloaded = (cc: PbLinkCostCenter) => !cc.preloaded;
export const isPbLinkJstRecordEmpty = (record: PbLinkJstRecord) => isNil(record.paragraph.paragraphId);
export const isPbLinkCsmRecordEmpty = (record: PbLinkCsmRecord) => isNil(record.heading.headingId);

export const getPbLinkCostCenterId = (cc: PbLinkCostCenter) => cc.id;
export const getPbLinkCcElementId = (el: PbLinkCcElement) => `${el.costCenterId}:${el.itemId}`;
export const getPbLinkRecordId = (record: PbLinkRecord) => record.recordId;

export const pbLinkCostCenterComparator = composeComparators<PbLinkCostCenter>(
  valueComparator('code'),
  valueComparator(getPbLinkCostCenterId));
export const pbLinkCcElementComparator = composeComparators<PbLinkCcElement>(
  valueComparator('code'),
  valueComparator(getPbLinkCcElementId));
export const pbLinkRecordComparator = composeComparators<PbLinkJstRecord>(
  valueComparator('name'),
  valueComparator(getPbLinkRecordId));

export const PB_LINK_CC_ELEMENT_ENTITY_CONFIG: EntityConfig<PbLinkCcElement> = {
  jstLayout: {
    validator: fieldValidatorFrom(nestedArrayValidator),
  },
  csmLayout: {
    validator: fieldValidatorFrom(nestedArrayValidator),
  },
};

export const PB_LINK_JST_RECORD_ENTITY_CONFIG: EntityConfig<PbLinkJstRecord> = {
  paragraph: {
    editable: true,
    required: false,
  },
  section: {
    editable: ({ section }: PbLinkJstRecord) => isNotNil(section.paragraphId),
  },
};

export const PB_LINK_CSM_RECORD_ENTITY_CONFIG: EntityConfig<PbLinkCsmRecord> = {
  heading: {
    editable: true,
    required: false,
  },
  subheading: {
    editable: ({ subheading }: PbLinkCsmRecord) => isNotNil(subheading.headingId),
    required: ({ subheading }: PbLinkCsmRecord) => isNotNil(subheading.headingId),
    validator: ({ subheading: { subheadingId, headingId } }: PbLinkCsmRecord) =>
      isNotNil(headingId) && isNil(subheadingId) ? { required: true } : void 0,
  },
  item: {
    editable: ({ item }: PbLinkCsmRecord) => isNotNil(item.subheadingId),
  },
};

export const validatePbLinkCcElementField = fieldValidatorByEntityConfig(PB_LINK_CC_ELEMENT_ENTITY_CONFIG);

export const pbLinkJstRecordFieldState = fieldStateByEntityConfig(PB_LINK_JST_RECORD_ENTITY_CONFIG);
export const validatePbLinkJstRecord = entityValidatorByEntityConfig(PB_LINK_JST_RECORD_ENTITY_CONFIG);
export const validatePbLinkJstRecordField = fieldValidatorByEntityConfig(PB_LINK_JST_RECORD_ENTITY_CONFIG);

export const pbLinkCsmRecordFieldState = fieldStateByEntityConfig(PB_LINK_CSM_RECORD_ENTITY_CONFIG);
export const validatePbLinkCsmRecord = entityValidatorByEntityConfig(PB_LINK_CSM_RECORD_ENTITY_CONFIG);
export const validatePbLinkCsmRecordField = fieldValidatorByEntityConfig(PB_LINK_CSM_RECORD_ENTITY_CONFIG);

export const pbLinkCcTable = createTableDriver<PbLinkCostCenter>(
  getPbLinkCostCenterId,
  createOrderIndexDriver('code', getPbLinkCostCenterId, stubTrue, pbLinkCostCenterComparator));
export const pbLinkCcElementTable = createTableDriver<PbLinkCcElement>(
  getPbLinkCcElementId,
  createGroupIndexDriver(
    'costCenter',
    getPbLinkCcElementId,
    stubTrue,
    (el) => el.costCenterId,
    pbLinkCcElementComparator),
  createGroupIndexDriver(
    'costCenterErrors',
    getPbLinkCcElementId,
    isEntityInvalid,
    (el: PbLinkCcElement) => el.costCenterId,
    pbLinkCcElementComparator));
export const pbLinkRecordTable = createTableDriver<PbLinkRecord>(
  getPbLinkRecordId,
  createOrderIndexDriver(
    'name',
    getPbLinkRecordId,
    stubTrue,
    pbLinkRecordComparator));
